import React, { useState, useEffect } from 'react';
import { DIMENSIONS, getRandomEquationForAnswer } from '../../constants/gameConstants';
import { renderShape } from '../../../utils/renderUtils.jsx';
import { breakpoints } from '../../../styles/theme';

const StimulusDisplay = ({ 
  currentStimulus, 
  activeDimensions, 
  stimulusVisible 
}) => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const isMobile = windowWidth < parseInt(breakpoints.mobile);
  
  // Helper function to get orbital circle position coordinates
  const getOrbitalPosition = (position, isActive, isMobile) => {
    if (!isActive) {
      // Default to top middle when orbitalPosition is not active
      const defaultOffset = isMobile ? '42px' : '112px';
      return { top: `-${defaultOffset}`, left: '50%', transform: 'translateX(-50%)' };
    }
    
    const offset = isMobile ? 42 : 112; // Equal distance from all sides
    const cornerOffset = isMobile ? 32 : 86; // Distance for diagonal corners
    
    const positions = {
      'N': { top: `-${offset}px`, left: '50%', transform: 'translateX(-50%)' },
      'NE': { top: `-${cornerOffset}px`, right: `-${cornerOffset}px` },
      'E': { top: '50%', right: `-${offset}px`, transform: 'translateY(-50%)' },
      'SE': { bottom: `-${cornerOffset}px`, right: `-${cornerOffset}px` },
      'S': { bottom: `-${offset}px`, left: '50%', transform: 'translateX(-50%)' },
      'SW': { bottom: `-${cornerOffset}px`, left: `-${cornerOffset}px` },
      'W': { top: '50%', left: `-${offset}px`, transform: 'translateY(-50%)' },
      'NW': { top: `-${cornerOffset}px`, left: `-${cornerOffset}px` }
    };
    return positions[position] || positions['N'];
  };

  // Helper function to get circle size in pixels
  const getCircleSize = (size, isMobile) => {
    const desktopSizes = {
      'tiny': 40,
      'small': 55,
      'medium': 70,
      'large': 85,
      'huge': 100
    };
    
    const mobileSizes = {
      'tiny': 18,
      'small': 25,
      'medium': 32,
      'large': 39,
      'huge': 46
    };
    
    const sizes = isMobile ? mobileSizes : desktopSizes;
    return sizes[size] || (isMobile ? 32 : 70);
  };

  // Check if any orbital dimension is active
  const hasOrbitalDimensions = activeDimensions.some(dim => 
    ['orbitalPosition', 'circleSize', 'circleRotation', 'circleBrightness'].includes(dim)
  );

  return (
    <div style={{
      padding: hasOrbitalDimensions ? (isMobile ? '42px 0.75rem 45px 0.75rem' : '132px') : '0',
      position: 'relative'
    }}>
      <div style={{
        background: 'rgba(30, 41, 59, 0.6)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        padding: isMobile ? (hasOrbitalDimensions ? '0.5rem' : '1.5rem 0.75rem') : '1.5rem 0.5rem',
        border: '1px solid rgba(100, 116, 139, 0.3)',
        position: 'relative'
      }}>
      {/* Border indicator */}
      {activeDimensions.includes('border') && stimulusVisible && (
        <div style={{
          position: 'absolute',
          ...(currentStimulus.border === 'top' && {
            top: 0,
            left: 0,
            right: 0,
            height: '8px',
            width: '100%'
          }),
          ...(currentStimulus.border === 'bottom' && {
            bottom: 0,
            left: 0,
            right: 0,
            height: '8px',
            width: '100%'
          }),
          ...(currentStimulus.border === 'left' && {
            left: 0,
            top: 0,
            bottom: 0,
            width: '8px',
            height: '100%'
          }),
          ...(currentStimulus.border === 'right' && {
            right: 0,
            top: 0,
            bottom: 0,
            width: '8px',
            height: '100%'
          }),
          background: DIMENSIONS.border.color,
          borderRadius: '16px',
          animation: 'pulse 0.5s ease-in-out'
        }} />
      )}

      {/* Orbital Circle - positioned around the board */}
      {hasOrbitalDimensions && stimulusVisible && (() => {
        // Only apply properties if their specific dimension is active
        const position = activeDimensions.includes('orbitalPosition') ? currentStimulus.orbitalPosition : 'N';
        const size = activeDimensions.includes('circleSize') ? currentStimulus.circleSize : 'medium';
        const brightness = activeDimensions.includes('circleBrightness') ? currentStimulus.circleBrightness : 0;
        const rotation = activeDimensions.includes('circleRotation') ? currentStimulus.circleRotation : 0;
        const showRotationIndicator = activeDimensions.includes('circleRotation');
        
        return (
          <div style={{
            position: 'absolute',
            ...getOrbitalPosition(position, activeDimensions.includes('orbitalPosition'), isMobile),
            width: `${getCircleSize(size, isMobile)}px`,
            height: `${getCircleSize(size, isMobile)}px`,
            borderRadius: '50%',
            background: `rgba(96, 165, 250, ${brightness})`,
            border: isMobile ? '2px solid rgba(255, 255, 255, 0.8)' : '3px solid rgba(255, 255, 255, 0.8)',
            boxShadow: `0 0 20px rgba(96, 165, 250, ${brightness * 0.8})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 10
          }}>
            {/* Rotation indicator line - only show if rotation dimension is active */}
            {showRotationIndicator && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '35%',
                height: isMobile ? '2px' : '3px',
                background: 'white',
                transformOrigin: '0% 50%',
                transform: `translate(0, -50%) rotate(${rotation - 90}deg)`,
                borderRadius: '2px',
                boxShadow: '0 0 4px rgba(0, 0, 0, 0.5)'
              }} />
            )}
            {/* Center dot */}
            <div style={{
              width: isMobile ? '6px' : '10px',
              height: isMobile ? '6px' : '10px',
              borderRadius: '50%',
              background: 'white',
              boxShadow: '0 0 4px rgba(0, 0, 0, 0.5)',
              zIndex: 1
            }} />
          </div>
        );
      })()}

      {/* Position Grid with integrated color, shape, and number */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: isMobile && hasOrbitalDimensions ? '0.3rem' : '0.75rem',
        maxWidth: isMobile && hasOrbitalDimensions ? '100%' : '423px',
        width: isMobile && hasOrbitalDimensions ? '100%' : 'auto',
        margin: '0 auto'
      }}>
        {[...Array(9)].map((_, i) => {
          // Check if any grid-based dimensions are active
          const gridDimensions = ['position', 'color', 'shape', 'number', 'face', 'arrow', 'movement', 'pattern'];
          const hasAnyGridDimension = activeDimensions.some(dim => gridDimensions.includes(dim));
          
          // If position is not active, always use center square (index 4) for visual elements
          const positionIsActive = activeDimensions.includes('position');
          const isActiveSquare = positionIsActive 
            ? currentStimulus.position === i 
            : (hasAnyGridDimension && i === 4); // Center square only if grid dimensions are active
          
          // Show visual dimensions on the active square
          const showShape = activeDimensions.includes('shape') && isActiveSquare && stimulusVisible;
          const showNumber = activeDimensions.includes('number') && isActiveSquare && stimulusVisible;
          const showFace = activeDimensions.includes('face') && isActiveSquare && stimulusVisible;
          const showArrow = activeDimensions.includes('arrow') && isActiveSquare && stimulusVisible;
          const showMovement = activeDimensions.includes('movement') && isActiveSquare && stimulusVisible;
          const showColor = activeDimensions.includes('color') && isActiveSquare && stimulusVisible;
          const showPattern = activeDimensions.includes('pattern') && isActiveSquare && stimulusVisible;
          
          // Determine cell color
          let cellColor;
          if (isActiveSquare && stimulusVisible) {
            if (showColor) {
              cellColor = currentStimulus.color;
            } else if (positionIsActive) {
              cellColor = '#60a5fa'; // Position dimension active, show blue
            } else {
              cellColor = 'rgba(100, 116, 139, 0.4)'; // Just a container for shape/number
            }
          } else {
            cellColor = 'rgba(100, 116, 139, 0.2)';
          }
          
          // Determine shape/number color based on background
          const isWhiteBackground = cellColor === '#ffffff';
          const isBlackBackground = cellColor === '#000000';
          const contentColor = isWhiteBackground ? '#000000' : isBlackBackground ? '#ffffff' : 'rgba(255, 255, 255, 0.9)';
          
          // Determine border style (pattern dimension)
          let borderStyle = 'solid';
          let borderWidth = '1px';
          let showZigzag = false;
          let showWavy = false;
          
          if (showPattern) {
            const pattern = currentStimulus.pattern;
            
            if (pattern === 'zigzag') {
              showZigzag = true;
              borderStyle = 'none'; // Hide regular border, show SVG
            } else if (pattern === 'wavy') {
              showWavy = true;
              borderStyle = 'none'; // Hide regular border, show SVG
            } else if (pattern === 'double') {
              borderStyle = 'double';
              borderWidth = '6px'; // Double lines need more width to be visible
            } else {
              // Standard CSS border styles: solid, dashed, dotted
              borderStyle = pattern;
              borderWidth = '3px';
            }
          } else if (isActiveSquare && stimulusVisible && positionIsActive) {
            borderWidth = '3px';
          }
          
          // Box shadow for position glow only
          const finalBoxShadow = (isActiveSquare && stimulusVisible && positionIsActive) 
            ? `0 0 30px ${cellColor}80`
            : 'none';
          
          // Add movement animation class name
          const animationStyle = showMovement ? `move-${currentStimulus.movement} 1.5s ease-in-out infinite` : 'none';
          
          // Border color
          const patternColor = isWhiteBackground ? '#94a3b8' : 'rgba(255, 255, 255, 0.8)';
          
          return (
            <div
              key={i}
              style={{
                aspectRatio: '1',
                background: cellColor,
                borderRadius: '12px',
                transition: 'all 0.2s',
                borderStyle: borderStyle,
                borderWidth: borderWidth,
                borderColor: isActiveSquare && stimulusVisible ? (isWhiteBackground ? '#cbd5e1' : 'rgba(255, 255, 255, 0.7)') : (isWhiteBackground ? '#94a3b8' : 'rgba(100, 116, 139, 0.3)'),
                boxShadow: finalBoxShadow,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                animation: animationStyle
              }}
            >
              {/* Zigzag pattern overlay */}
              {showZigzag && (
                <svg
                  style={{
                    position: 'absolute',
                    top: '-2px',
                    left: '-2px',
                    right: '-2px',
                    bottom: '-2px',
                    width: 'calc(100% + 4px)',
                    height: 'calc(100% + 4px)',
                    pointerEvents: 'none',
                    overflow: 'visible'
                  }}
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M 0,0 L 5,3 L 10,0 L 15,3 L 20,0 L 25,3 L 30,0 L 35,3 L 40,0 L 45,3 L 50,0 L 55,3 L 60,0 L 65,3 L 70,0 L 75,3 L 80,0 L 85,3 L 90,0 L 95,3 L 100,0 L 100,3 L 0,3 Z M 100,0 L 97,5 L 100,10 L 97,15 L 100,20 L 97,25 L 100,30 L 97,35 L 100,40 L 97,45 L 100,50 L 97,55 L 100,60 L 97,65 L 100,70 L 97,75 L 100,80 L 97,85 L 100,90 L 97,95 L 100,100 L 97,100 L 100,0 Z M 100,100 L 95,97 L 90,100 L 85,97 L 80,100 L 75,97 L 70,100 L 65,97 L 60,100 L 55,97 L 50,100 L 45,97 L 40,100 L 35,97 L 30,100 L 25,97 L 20,100 L 15,97 L 10,100 L 5,97 L 0,100 L 0,97 L 100,97 Z M 0,100 L 3,95 L 0,90 L 3,85 L 0,80 L 3,75 L 0,70 L 3,65 L 0,60 L 3,55 L 0,50 L 3,45 L 0,40 L 3,35 L 0,30 L 3,25 L 0,20 L 3,15 L 0,10 L 3,5 L 0,0 L 3,0 L 0,100 Z"
                    fill={patternColor}
                    stroke="none"
                  />
                </svg>
              )}
              
              {/* Wavy pattern overlay */}
              {showWavy && (
                <svg
                  style={{
                    position: 'absolute',
                    top: '-2px',
                    left: '-2px',
                    right: '-2px',
                    bottom: '-2px',
                    width: 'calc(100% + 4px)',
                    height: 'calc(100% + 4px)',
                    pointerEvents: 'none',
                    overflow: 'visible'
                  }}
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M 0,0 Q 5,3 10,0 T 20,0 T 30,0 T 40,0 T 50,0 T 60,0 T 70,0 T 80,0 T 90,0 T 100,0 L 100,3 Q 95,0 90,3 T 80,3 T 70,3 T 60,3 T 50,3 T 40,3 T 30,3 T 20,3 T 10,3 T 0,3 Z M 100,0 Q 97,5 100,10 T 100,20 T 100,30 T 100,40 T 100,50 T 100,60 T 100,70 T 100,80 T 100,90 T 100,100 L 97,100 Q 100,95 97,90 T 97,80 T 97,70 T 97,60 T 97,50 T 97,40 T 97,30 T 97,20 T 97,10 T 97,0 Z M 100,100 Q 95,97 90,100 T 80,100 T 70,100 T 60,100 T 50,100 T 40,100 T 30,100 T 20,100 T 10,100 T 0,100 L 0,97 Q 5,100 10,97 T 20,97 T 30,97 T 40,97 T 50,97 T 60,97 T 70,97 T 80,97 T 90,97 T 100,97 Z M 0,100 Q 3,95 0,90 T 0,80 T 0,70 T 0,60 T 0,50 T 0,40 T 0,30 T 0,20 T 0,10 T 0,0 L 3,0 Q 0,5 3,10 T 3,20 T 3,30 T 3,40 T 3,50 T 3,60 T 3,70 T 3,80 T 3,90 T 3,100 Z"
                    fill={patternColor}
                    stroke="none"
                  />
                </svg>
              )}
              
              {/* Calculate what to show in center */}
              {(() => {
                const centerItems = [];
                if (showShape) centerItems.push('shape');
                if (showNumber) centerItems.push('number');
                if (showFace) centerItems.push('face');
                
                const itemCount = centerItems.length;
                
                if (itemCount === 0) return null;
                
                // Single item - display large (but check if arrow is also present)
                if (itemCount === 1) {
                  const hasArrow = showArrow;
                  const sizeFactor = hasArrow ? 0.65 : 0.8;
                  const fontSize = hasArrow ? '3rem' : '4rem';
                  
                  if (showShape) {
                    return (
                      <svg width={`${sizeFactor * 100}%`} height={`${sizeFactor * 100}%`} viewBox="0 0 100 100" style={{
                        filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))',
                        maxWidth: '100%',
                        maxHeight: '100%'
                      }}>
                        {renderShape(currentStimulus.shape, contentColor)}
                      </svg>
                    );
                  }
                  if (showNumber) {
                    return (
                      <div style={{
                        fontSize: fontSize,
                        fontWeight: '800',
                        color: contentColor,
                        textShadow: isWhiteBackground ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.3)'
                      }}>
                        {currentStimulus.number}
                      </div>
                    );
                  }
                  if (showFace) {
                    return (
                      <div style={{
                        fontSize: fontSize,
                        lineHeight: '1'
                      }}>
                        {currentStimulus.face}
                      </div>
                    );
                  }
                }
                
                // Two items - stack vertically
                if (itemCount === 2) {
                  const hasArrow = showArrow;
                  const sizeFactor = hasArrow ? 0.35 : 0.5;
                  const fontSize = hasArrow ? '1.5rem' : '2rem';
                  const faceFontSize = hasArrow ? '1.75rem' : '2.5rem';
                  const gap = hasArrow ? '0.25rem' : '0.5rem';
                  
                  return (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: gap,
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}>
                      {showShape && (
                        <svg width={`${sizeFactor * 100}%`} height={`${sizeFactor * 100}%`} viewBox="0 0 100 100" style={{
                          filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))',
                          maxWidth: '100%',
                          flexShrink: 1
                        }}>
                          {renderShape(currentStimulus.shape, contentColor)}
                        </svg>
                      )}
                      {showNumber && (
                        <div style={{
                          fontSize: fontSize,
                          fontWeight: '800',
                          color: contentColor,
                          textShadow: isWhiteBackground ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.3)',
                          lineHeight: '1'
                        }}>
                          {currentStimulus.number}
                        </div>
                      )}
                      {showFace && (
                        <div style={{
                          fontSize: faceFontSize,
                          lineHeight: '1'
                        }}>
                          {currentStimulus.face}
                        </div>
                      )}
                    </div>
                  );
                }
                
                // Three items - stack vertically, smaller
                if (itemCount === 3) {
                  const hasArrow = showArrow;
                  const sizeFactor = hasArrow ? 0.25 : 0.35;
                  const fontSize = hasArrow ? '1.1rem' : '1.5rem';
                  const faceFontSize = hasArrow ? '1.3rem' : '1.75rem';
                  const gap = hasArrow ? '0.15rem' : '0.25rem';
                  
                  return (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: gap,
                      maxWidth: '100%',
                      maxHeight: '100%',
                      padding: hasArrow ? '0.25rem' : '0'
                    }}>
                      {showShape && (
                        <svg width={`${sizeFactor * 100}%`} height={`${sizeFactor * 100}%`} viewBox="0 0 100 100" style={{
                          filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))',
                          maxWidth: '100%',
                          flexShrink: 1
                        }}>
                          {renderShape(currentStimulus.shape, contentColor)}
                        </svg>
                      )}
                      {showNumber && (
                        <div style={{
                          fontSize: fontSize,
                          fontWeight: '800',
                          color: contentColor,
                          textShadow: isWhiteBackground ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.3)',
                          lineHeight: '1'
                        }}>
                          {currentStimulus.number}
                        </div>
                      )}
                      {showFace && (
                        <div style={{
                          fontSize: faceFontSize,
                          lineHeight: '1'
                        }}>
                          {currentStimulus.face}
                        </div>
                      )}
                    </div>
                  );
                }
              })()}
              
              {/* Arrow in corner - separate from center elements */}
              {showArrow && (
                <div style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  fontSize: '1.5rem',
                  lineHeight: '1',
                  color: contentColor,
                  fontWeight: 'bold',
                  textShadow: isWhiteBackground ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.5)'
                }}>
                  {currentStimulus.arrow}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Text Dimensions Display (below grid) */}
      {(activeDimensions.includes('categoryText') || 
        activeDimensions.includes('nameText') || 
        activeDimensions.includes('math')) && (
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '1.5rem',
          minHeight: '60px'
        }}>
          {/* Category Text */}
          {activeDimensions.includes('categoryText') && stimulusVisible && (
            <div style={{
              background: 'rgba(236, 72, 153, 0.2)',
              border: `3px solid ${DIMENSIONS.categoryText.color}`,
              borderRadius: '12px',
              padding: '0.75rem 1.5rem',
              fontSize: '1.5rem',
              fontWeight: '800',
              color: '#e2e8f0',
              letterSpacing: '0.05em',
              boxShadow: `0 0 20px ${DIMENSIONS.categoryText.color}40`
            }}>
              {currentStimulus.categoryText}
            </div>
          )}

          {/* Name Text */}
          {activeDimensions.includes('nameText') && stimulusVisible && (
            <div style={{
              background: 'rgba(139, 92, 246, 0.2)',
              border: `3px solid ${DIMENSIONS.nameText.color}`,
              borderRadius: '12px',
              padding: '0.75rem 1.5rem',
              fontSize: '1.5rem',
              fontWeight: '800',
              color: '#e2e8f0',
              letterSpacing: '0.05em',
              boxShadow: `0 0 20px ${DIMENSIONS.nameText.color}40`
            }}>
              {currentStimulus.nameText}
            </div>
          )}

          {/* Math Equation */}
          {activeDimensions.includes('math') && stimulusVisible && (
            <div style={{
              background: 'rgba(234, 179, 8, 0.2)',
              border: `3px solid ${DIMENSIONS.math.color}`,
              borderRadius: '12px',
              padding: '0.75rem 1.5rem',
              fontSize: '1.5rem',
              fontWeight: '800',
              color: '#e2e8f0',
              letterSpacing: '0.05em',
              boxShadow: `0 0 20px ${DIMENSIONS.math.color}40`,
              fontFamily: 'monospace'
            }}>
              {currentStimulus.mathDisplay || '1-1'} = ?
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default StimulusDisplay;

